"use client";

import { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { badgeVariants } from "@/components/ui/badge";
import { CarDetails } from "@/lib/actions/car-details";
import { CircleDollarSign, Sparkles, Tag, Calendar, ShoppingBag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { requestTestDrive, makeOffer } from "@/lib/actions/car-details";

const testDriveSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(6, "Phone number is required"),
  date: z.string().min(1, "Please select a date"),
  time: z.string().min(1, "Please select a time"),
  notes: z.string().optional()
});

const offerSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(6, "Phone number is required"),
  amount: z.string().min(1, "Please enter an offer amount"),
  message: z.string().optional()
});

interface PriceCardProps {
  car: CarDetails;
}

export function PriceCard({ car }: PriceCardProps) {
  const [isTestDriveDialogOpen, setIsTestDriveDialogOpen] = useState(false);
  const [isOfferDialogOpen, setIsOfferDialogOpen] = useState(false);
  const [isTestDriveSubmitted, setIsTestDriveSubmitted] = useState(false);
  const [isOfferSubmitted, setIsOfferSubmitted] = useState(false);
  
  // Pricing info
  const currency = car.carPricing?.currency || "KES";
  const sellingPrice = car.carPricing?.sellingPrice || 0;
  const formattedPrice = formatCurrency(sellingPrice, currency);
  const msrp = car.carPricing?.msrp || 0;
  const formattedMsrp = msrp > 0 ? formatCurrency(msrp, currency) : null;
  const discount = msrp > sellingPrice ? msrp - sellingPrice : 0;
  const discountPercentage = msrp > 0 ? Math.round((discount / msrp) * 100) : 0;
  const isNegotiable = car.carPricing?.negotiable || false;
  const warranty = car.carPricing?.warranty || null;

  // Extract seller contact information
  const sellerEmail = car.organization?.email || car.organization?.organizationEmail || 'info@kashiandkarz.com';
  const sellerPhone = car.organization?.phone || car.organization?.phoneNumber || car.organization?.contact?.phone || null;

  // Test Drive Form
  const testDriveForm = useForm<z.infer<typeof testDriveSchema>>({
    resolver: zodResolver(testDriveSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      notes: ""
    }
  });

  const onTestDriveSubmit = async (data: z.infer<typeof testDriveSchema>) => {
    try {
      const result = await requestTestDrive({
        carId: car.id,
        ...data
      });
      
      if (result.success) {
        setIsTestDriveSubmitted(true);
        toast.success("Test drive request submitted", {
          description: "We'll get back to you soon to confirm your test drive"
        });
      } else {
        throw new Error(result.error || "Failed to submit test drive request");
      }
    } catch (error) {
      toast.error("Error", { 
        description: "There was a problem submitting your test drive request"
      });
    }
  };
  
  // Offer Form
  const offerForm = useForm<z.infer<typeof offerSchema>>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      amount: String(Math.round(sellingPrice * 0.9)),
      message: ""
    }
  });

  const onOfferSubmit = async (data: z.infer<typeof offerSchema>) => {
    try {
      const result = await makeOffer({
        carId: car.id,
        ...data
      });
      
      if (result.success) {
        setIsOfferSubmitted(true);
        toast.success("Offer submitted", {
          description: "We've sent your offer to the seller"
        });
      } else {
        throw new Error(result.error || "Failed to submit offer");
      }
    } catch (error) {
      toast.error("Error", { 
        description: "There was a problem submitting your offer"
      });
    }
  };
  
  // Reset form states
  const handleDialogClose = (dialogType: 'testDrive' | 'offer') => {
    if (dialogType === 'testDrive') {
      if (!isTestDriveSubmitted) {
        testDriveForm.reset();
      }
      setIsTestDriveDialogOpen(false);
    } else {
      if (!isOfferSubmitted) {
        offerForm.reset();
      }
      setIsOfferDialogOpen(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative rounded-xl overflow-hidden glossy-card"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
      
      <div className="p-6 relative z-10 space-y-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <CircleDollarSign className="h-4 w-4" />
          <span className="text-sm">Price</span>
        </div>
        
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <h2 className="text-3xl font-bold text-foreground neon-text">{formattedPrice}</h2>
            {formattedMsrp && (
              <span className="text-muted-foreground line-through text-sm">{formattedMsrp}</span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 mt-1">
            {discount > 0 && (
              <span className={badgeVariants({ variant: "outline", className: "bg-green-500/10 text-green-500 border-green-500/20" })}>
                <Sparkles className="mr-1 h-3 w-3" /> Save {discountPercentage}%
              </span>
            )}
            {isNegotiable && (
              <span className={badgeVariants({ variant: "outline", className: "bg-primary/10 text-primary border-primary/20" })}>
                <Tag className="mr-1 h-3 w-3" /> Negotiable
              </span>
            )}
          </div>
          
          {warranty && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-primary font-medium">Warranty:</span> {warranty}
            </p>
          )}
        </div>
        
        <div className="pt-4 border-t border-muted/20 space-y-3">
          <Button className="w-full rounded-full" size="lg" asChild>
            <Link href={`mailto:${sellerEmail}?subject=Inquiry about ${car.year} ${car.make?.name} ${car.model?.name}`}>
              Contact Seller
            </Link>
          </Button>
          
          {sellerPhone && (
            <a href={`tel:${sellerPhone}`} className="text-primary w-full text-sm">
              {sellerPhone}
            </a>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <Dialog open={isTestDriveDialogOpen} onOpenChange={(open) => {
              if (!open) handleDialogClose('testDrive');
              else setIsTestDriveDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full hover-glow">
                  <Calendar className="mr-2 h-4 w-4" />
                  Test Drive
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] border-primary/20 neon-glow bg-background/95 backdrop-blur-sm">
                {!isTestDriveSubmitted ? (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-center text-xl">Schedule Test Drive</DialogTitle>
                      <DialogDescription className="text-center pt-2">
                        Schedule a test drive for the {car.year} {car.make?.name} {car.model?.name}
                      </DialogDescription>
                    </DialogHeader>
                    <Form {...testDriveForm}>
                      <form onSubmit={testDriveForm.handleSubmit(onTestDriveSubmit)} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={testDriveForm.control}
                            name="date"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Preferred Date</FormLabel>
                                <FormControl>
                                  <Input 
                                    type="date" 
                                    min={new Date().toISOString().split('T')[0]}
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={testDriveForm.control}
                            name="time"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Preferred Time</FormLabel>
                                <FormControl>
                                  <Input type="time" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={testDriveForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={testDriveForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="Your email" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={testDriveForm.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Phone</FormLabel>
                                <FormControl>
                                  <Input placeholder="Your phone number" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={testDriveForm.control}
                          name="notes"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Additional Notes</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Any special requests or questions" 
                                  className="resize-none min-h-[80px]"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <DialogFooter className="mt-6">
                          <Button type="submit" className="w-full rounded-full">
                            Schedule Test Drive
                          </Button>
                        </DialogFooter>
                      </form>
                    </Form>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="bg-primary/10 p-4 rounded-full mb-4">
                      <Calendar className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Test Drive Requested!</h3>
                    <p className="text-muted-foreground mb-6">
                      We'll contact you soon to confirm your test drive appointment.
                    </p>
                    <Button 
                      variant="outline" 
                      className="rounded-full"
                      onClick={() => {
                        setIsTestDriveDialogOpen(false);
                        setIsTestDriveSubmitted(false);
                        testDriveForm.reset();
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            <Dialog open={isOfferDialogOpen} onOpenChange={(open) => {
              if (!open) handleDialogClose('offer');
              else setIsOfferDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full hover-glow">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Make Offer
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] border-primary/20 neon-glow bg-background/95 backdrop-blur-sm">
                {!isOfferSubmitted ? (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-center text-xl">Make an Offer</DialogTitle>
                      <DialogDescription className="text-center pt-2">
                        Submit your offer for the {car.year} {car.make?.name} {car.model?.name}
                      </DialogDescription>
                    </DialogHeader>
                    <Form {...offerForm}>
                      <form onSubmit={offerForm.handleSubmit(onOfferSubmit)} className="space-y-4 mt-4">
                        <FormField
                          control={offerForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={offerForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="Your email" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={offerForm.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Phone</FormLabel>
                                <FormControl>
                                  <Input placeholder="Your phone number" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={offerForm.control}
                          name="amount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your Offer ({currency})</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  placeholder="Enter your offer amount" 
                                  min={1}
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={offerForm.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Message (Optional)</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Any additional information about your offer" 
                                  className="resize-none min-h-[80px]"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <DialogFooter className="mt-6">
                          <Button type="submit" className="w-full rounded-full">
                            Submit Offer
                          </Button>
                        </DialogFooter>
                      </form>
                    </Form>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="bg-primary/10 p-4 rounded-full mb-4">
                      <ShoppingBag className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Offer Submitted!</h3>
                    <p className="text-muted-foreground mb-6">
                      Thank you for your offer. The seller will review it and get back to you soon.
                    </p>
                    <Button 
                      variant="outline" 
                      className="rounded-full"
                      onClick={() => {
                        setIsOfferDialogOpen(false);
                        setIsOfferSubmitted(false);
                        offerForm.reset();
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </motion.div>
  );
} 